"use client";

import * as React from "react";
import Image from "next/image";
import Dialog from "src/components/Dialog";
import AsoebiModal from "@src/components/AsoebiModal";
import { shimmer, toBase64 } from "@src/utils/shimmer";
import {
  EventBySlugQuery,
  GetAlbumDetailsQuery,
  GetHeroImageQuery,
} from "@src/@types/api.d";
import defaultImg from "../../../../public/images/bw-flower.jpeg";
import { AvailablePaymentCurrencies } from "@src/components/BirthdayTemplate1/AsoEbiLink";

interface Props {
  eventUuid: string | undefined;
  eventId: number;
  showAsoebi?: boolean;
  showCashGift?: boolean;
  isCashGiftSetup: boolean;
  availableGiftPaymentCurrencies?: AvailablePaymentCurrencies;
  albumDetailsData?: GetAlbumDetailsQuery | null;
  heroImageData?: GetHeroImageQuery | null;
  eventBySlugData?: EventBySlugQuery | null;
}

const AsoEbi: React.FC<Props> = ({
  eventUuid,
  showAsoebi,
  albumDetailsData,
  heroImageData,
  eventBySlugData,
}) => {
  const [openAsoebi, setOpenAsoebi] = React.useState(false);

  const validAlbumImages = React.useMemo(() => {
    return (albumDetailsData?.getMedia ?? []).filter(
      (m) =>
        m &&
        typeof m?.type === "string" &&
        m.type.toLowerCase().includes("image") &&
        m?.meta?.s3_url?.startsWith("http")
    );
  }, [albumDetailsData]);

  const leftImage =
    validAlbumImages[0]?.meta?.s3_url ||
    heroImageData?.getHeroImage ||
    eventBySlugData?.eventBySlug?.hero_image_thumbnail ||
    defaultImg;

  const rightImage =
    validAlbumImages[1]?.meta?.s3_url ||
    eventBySlugData?.eventBySlug?.pass_image ||
    defaultImg;

  if (!showAsoebi) {
    return null;
  }

  return (
    <section className="w-full py-6 md:py-8 min-h-[calc(105vh-var(--t22-header-height))] bg-[#E8EEF6]">
      <div className="max-w-5xl mx-auto px-4 md:px-8 lg:px-20">
        <div className="mb-8 md:mb-10 text-center">
          <h2 className="font-bauerBodoni text-2xl md:text-4xl font-light text-[#071A2F] uppercase tracking-wider">
            Our Asoebi
          </h2>
        </div>

        <article className="border border-[#fff] p-2">
          <div className="bg-white border border-[#123B66]/20 shadow-xl flex flex-col lg:flex-row overflow-hidden">
            <div className="lg:w-[45%] p-6 border-b lg:border-b-0 lg:border-r border-[#123B66]/10">
              <div className="grid grid-cols-2 gap-4 md:gap-6">
                <div className="relative w-full h-56 md:h-72 overflow-hidden rounded-[40%_60%_60%_40%/40%_40%_60%_60%]">
                  <Image
                    src={leftImage}
                    alt="Couple photo one"
                    layout="fill"
                    objectFit="cover"
                    objectPosition="center top"
                    placeholder="blur"
                    blurDataURL={`data:image/svg+xml;base64,${toBase64(
                      shimmer(360, 520)
                    )}`}
                    onError={(event) => {
                      const target = event.currentTarget as HTMLImageElement;
                      if (!target.src.includes("bw-flower.jpeg")) {
                        target.src = defaultImg.src;
                      }
                    }}
                  />
                </div>
                <div className="relative w-full h-56 md:h-72 overflow-hidden rounded-[60%_40%_40%_60%/40%_40%_60%_60%] mt-8 md:mt-10">
                  <Image
                    src={rightImage}
                    alt="Couple photo two"
                    layout="fill"
                    objectFit="cover"
                    objectPosition="center top"
                    placeholder="blur"
                    blurDataURL={`data:image/svg+xml;base64,${toBase64(
                      shimmer(360, 520)
                    )}`}
                    onError={(event) => {
                      const target = event.currentTarget as HTMLImageElement;
                      if (!target.src.includes("bw-flower.jpeg")) {
                        target.src = defaultImg.src;
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="lg:w-[55%] p-6 md:p-8 flex flex-col justify-center gap-6">
              <div className="bg-white rounded-xl py-5 md:py-6">
                <h3 className="text-2xl md:text-3xl text-[#071A2F] font-bauerBodoni leading-tight  tracking-wide mb-3">
                  Celebrate with us in style!
                </h3>
                <p className="text-sm md:text-base text-[#071A2F] font-openSans opacity-80 leading-relaxed">
                  Explore our curated Asoebi options and place your order for
                  the big day.
                </p>
              </div>

              <Dialog
                trigger={
                  <button
                    className="inline-flex items-center justify-center w-[170px] px-4 py-3 bg-white text-[#123B66] border border-[#123B66] rounded-full font-bauerBodoni uppercase tracking-wider text-sm hover:bg-[#123B66] hover:text-[#E8EEF6] transition-all duration-300 shadow-lg hover:shadow-xl font-black"
                    type="button"
                  >
                    Get Your Asoebi
                  </button>
                }
                open={openAsoebi}
                onOpenChange={setOpenAsoebi}
              >
                <AsoebiModal eventUuid={eventUuid} setOpen={setOpenAsoebi} />
              </Dialog>
            </div>
          </div>
        </article>
      </div>
    </section>
  );
};

export default AsoEbi;
